import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatKey, isValidWidth, isValidX } from '~/common/charts/helpers';
import type { ChartPoint } from '~/common/charts/types';

type BarChartProps = {
  data: Array<ChartPoint>;
  title?: string;
  timeRangeLabel?: string;
  yAxisLabel?: string;
  yAxisFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => [string, string];
};

export default function SimpleBarChart({
  data,
  title,
  timeRangeLabel,
  yAxisLabel,
  yAxisFormatter,
  tooltipFormatter,
}: BarChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  const chartData = data.map((point) => ({
    key: formatKey(point.key),
    value: point.value,
    ...point.metadata,
  }));

  return (
    <div className="h-full p-1">
      {title && (
        <div className="flex flex-col pb-4">
          <div className=" text-base font-medium">{title}</div>
          {timeRangeLabel && <div className="text-2xs text-gray-500">{timeRangeLabel}</div>}
        </div>
      )}

      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 10, left: 15, bottom: 20 }}
          onMouseMove={(state) => {
            setActiveIndex(state.activeTooltipIndex);
          }}
          onMouseLeave={() => setActiveIndex(undefined)}
        >
          <CartesianGrid horizontal={true} vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis
            dataKey="key"
            fontSize={10}
            textAnchor="middle"
            tick={{ fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
            padding={{ left: 0 }}
          />
          <YAxis
            width={30}
            dataKey="value"
            fontSize={12}
            tick={{ fill: '#6B7280' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={yAxisFormatter}
          />
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ payload }) => {
              if (!payload?.length) return null;

              const value = tooltipFormatter ? tooltipFormatter(Number(payload[0].value))[0] : payload[0].value;

              return (
                <div className="border bg-white p-2">
                  <div className="text-gray-500">{payload[0].name || yAxisLabel}</div>
                  <div>{value}</div>
                </div>
              );
            }}
          />
          <Bar
            dataKey="value"
            fill={'#4472BD'}
            radius={[4, 4, 0, 0]}
            barSize={60}
            name={yAxisLabel}
            label={{
              content: (props) => {
                const { x, width, index } = props;

                if (!isValidX(x) || !isValidWidth(width)) {
                  return null;
                }

                if (activeIndex === index) {
                  return null;
                  // <text
                  //   x={x + width / 2}
                  //   y={y}
                  //   dy={-10}
                  //   fill="#6B7280"
                  //   fontSize={14}
                  //   textAnchor="middle"
                  // >
                  //   {value}
                  // </text>
                }
                return null;
              },
            }}
          >
            {data.map((_, index) => (
              <Cell
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={`cell-${index}`}
                fillOpacity={activeIndex === undefined || activeIndex === index ? 1 : 0.5}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// - #33568E darkblue
// - #4472BD medblue
// - #DAE3F2 lightblue
// - #EBECED grey
//     - then onwards in gr
